import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import LogoutButton from '../../components/common/LogoutButton.jsx';
import { adminService } from '../../services/adminService.js';
import { oktaService } from '../../services/oktaService.js';
import { markLastLogin } from '../../services/userService';
import appInsightsLogger from '../../utils/appInsights.js';
import './AdminDashboard.css';
import OktaUserRegistration from '../../components/Admin/OktaUserRegistration.jsx';

function AdminDashboard({ userDetails = {} }) {
  const [allUsers, setAllUsers] = useState([]); // Complete dataset loaded from API
  const [users, setUsers] = useState([]); // Filtered results for display
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('Username');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(false);
  const [adminActivities, setAdminActivities] = useState([]);
  const [pageSize] = useState(5000); // Large pageSize to get all users (reduced from 10000 for better performance)
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

    const [userPage, setUserPage] = useState(1);
    const [userPageSize, setUserPageSize] = useState(10);
    const [userTotalPages, setUserTotalPages] = useState(1);
    const [displayedUsers, setDisplayedUsers] = useState([]);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [editingUser, setEditingUser] = useState(null); // User being edited

    // Refresh control state
    const [isPerformingAction, setIsPerformingAction] = useState(false);
    const [periodicRefreshEnabled, setPeriodicRefreshEnabled] = useState(false); // Start disabled, enable after initial load
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    // Helper function to determine if user is external (non-evicore) based on isExternal property or email domain
    const getIsExternal = (user) => {
        if (user?.isExternal !== null && user?.isExternal !== undefined) {
            return user.isExternal === true;
        }
        // Fallback to email domain check if isExternal is null/undefined
        const email = user?.email?.toLowerCase() || '';
        return !email.includes('@evicore.com');
    };

    // Constants for duplicated style literals
    const PADDING_SMALL = '4px 8px';
    const CURSOR_POINTER = 'pointer';
    const CURSOR_NOT_ALLOWED = 'not-allowed';
    const BORDER_RADIUS_SMALL = '4px';
    const BORDER_STANDARD = '1px solid #ccc';

    // Get admin user display name
    const getAdminDisplayName = () => {
        if (userDetails.firstName && userDetails.lastName) {
            return `Admin: ${userDetails.firstName} ${userDetails.lastName}`;
        }
        if (userDetails.emailId) {
            // Fallback to email-based name extraction
            const emailName = userDetails.emailId.split('@')[0];
            const nameParts = emailName.split('.');
            const firstName = nameParts[0] ? nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1) : '';
            const lastName = nameParts[1] ? nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1) : '';
            return `Admin: ${firstName} ${lastName}`.trim();
        }
        return 'Admin User';
    };

    // Load all users from API (complete dataset)
    const loadAllUsers = useCallback(async () => {
        try {
            const searchRequest = {
                query: '', // Empty to get all users
                searchBy: 'Username',
                status: '', // Empty to get all statuses
                pageNumber: 1,
                pageSize: pageSize // Large pageSize to get all users
            };

            const result = await oktaService.searchUsers(searchRequest);
            console.log('AdminDashboard: API response received:', result);

            if (result && Array.isArray(result)) {
                // Ensure firstName and lastName are present in allUsers (from API)
                const usersWithNames = result.map(user => ({
                    ...user,
                    firstName: user.firstName ?? '',
                    lastName: user.lastName ?? ''
                }));
                const sortedUsers = [...usersWithNames].sort((a, b) => {
                    const aIsExternal = getIsExternal(a);
                    const bIsExternal = getIsExternal(b);
                    if (aIsExternal && !bIsExternal) return -1;
                    if (!aIsExternal && bIsExternal) return 1;
                    return (a.username || '').localeCompare(b.username || '');
                });
                setAllUsers(sortedUsers);

                // Return sorted users for immediate use by callers
                return sortedUsers;
            } else {
                console.warn('Load all users returned unexpected data:', result);
                setAllUsers([]);
                setUsers([]);
                throw new Error('Invalid API response format');
            }
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'loadAllUsers'
            });

            console.error('Error loading all users via API:', error);

            // Set empty arrays on error
            setAllUsers([]);
            setUsers([]);
            throw error;
        }
    }, [pageSize]);

    // Optimistic update helper - instantly updates UI before API refresh
    const updateUserStatusOptimistically = useCallback((oktaUserId, newStatus) => {
        const updateUserInList = (userList) => 
            userList.map(user => 
                user.oktaUserId === oktaUserId 
                    ? { ...user, oktaStatus: newStatus, status: newStatus }
                    : user
            );
        
        setAllUsers(prev => updateUserInList(prev));
        setUsers(prev => updateUserInList(prev));
        setDisplayedUsers(prev => updateUserInList(prev));
    }, []);

    // Client-side search/filter function
    const performClientSideSearch = useCallback((sourceUsers, keepCurrentPage = false) => {
        const usersToFilter = sourceUsers || allUsers;
        let filtered = [...usersToFilter];

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            switch (searchBy) {
                case 'Username':
                    filtered = filtered.filter(u =>
                        (u.username || '').toLowerCase().includes(query)
                    );
                    break;
                case 'Email':
                    filtered = filtered.filter(u =>
                        (u.email || '').toLowerCase().includes(query)
                    );
                    break;
                case 'Organization':
                    filtered = filtered.filter(u =>
                        (u.organization || '').toLowerCase().includes(query)
                    );
                    break;
                default:
                    // Multi-field search fallback
                    filtered = filtered.filter(u =>
                        (u.username || '').toLowerCase().includes(query) ||
                        (u.email || '').toLowerCase().includes(query) ||
                        (u.userId?.toString() || '').includes(query)
                    );
                    break;
            }
        }

        // Filter by status - check both status and oktaStatus fields
        if (statusFilter !== 'All') {
            filtered = filtered.filter(u => {
                const statusLower = (u.oktaStatus || u.status || '').toLowerCase();
                const isLocked = u.isLocked || statusLower === 'locked' || statusLower === 'locked_out';
                if (statusFilter === 'Locked') {
                    return isLocked;
                }
                return statusLower === statusFilter.toLowerCase();
            });
        }

        // Sort: External users (non-evicore) first, then internal users (evicore), then by username
        filtered.sort((a, b) => {
            const aIsExternal = getIsExternal(a);
            const bIsExternal = getIsExternal(b);

            // External users (non-evicore) should appear first (return -1)
            if (aIsExternal && !bIsExternal) {
                return -1;
            }
            if (!aIsExternal && bIsExternal) {
                return 1;
            }
            // If both are same type, sort by username alphabetically
            return (a.username || '').localeCompare(b.username || '');
        });

        setUsers(filtered);

        // Update pagination
        const totalPages = Math.ceil(filtered.length / userPageSize);
        setUserTotalPages(totalPages);

        // Maintain current page if requested, otherwise reset to page 1
        const currentPageToUse = keepCurrentPage ? Math.min(userPage, totalPages || 1) : 1;
        const startIndex = (currentPageToUse - 1) * userPageSize;
        const endIndex = startIndex + userPageSize;
        setDisplayedUsers(filtered.slice(startIndex, endIndex));
        setUserPage(currentPageToUse);

        console.log('AdminDashboard: Client-side search completed. Filtered', filtered.length, 'users from', usersToFilter.length, 'total');
    }, [searchQuery, searchBy, statusFilter, userPageSize, userPage, allUsers]);

    const loadAdminActivities = useCallback(async (page = activityPage, pageSize = activityPageSize) => {
        try {
            appInsightsLogger.trackEvent('Admin_Load_Activities_Attempt', {
                component: 'AdminDashboard',
                page: page,
                pageSize: pageSize
            });

            const response = await adminService.getPaginatedAdminActivities(page, pageSize, 7);

            const responseData = response?.data || response;
            if (responseData?.activities && Array.isArray(responseData.activities)) {
                setAdminActivities(responseData.activities);
                setActivityTotalPages(responseData.totalPages || 1);

                appInsightsLogger.trackEvent('Admin_Load_Activities_Success', {
                    component: 'AdminDashboard',
                    activitiesCount: responseData.activities.length,
                    totalPages: responseData.totalPages
                });
            } else {
                console.warn('Admin activities returned unexpected data:', response);
                setAdminActivities([]);
                throw new Error('Invalid admin activities API response format');
            }
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'loadAdminActivities',
                page: page,
                pageSize: pageSize
            });
            console.error('Error loading admin activities via API:', error);
            setAdminActivities([]);
            throw error;
        }
    }, [activityPage, activityPageSize]);

    // Initialize data on mount
    useEffect(() => {
        // Prevent multiple initializations
        if (hasInitialized.current) {
            return;
        }

        const initializeData = async () => {
            hasInitialized.current = true;
            setLoading(true);

            try {
                const promises = [];
                // Mark last login for admin user
                if (userDetails?.userId && userDetails?.emailId) {
                    const markLastLoginDto = {
                        userId: userDetails.userId,
                        email: userDetails.emailId
                    };
                    promises.push(markLastLogin(markLastLoginDto));
                }
                
                // Load all users (complete dataset)
                promises.push(
                    loadAllUsers().catch(error => {
                        appInsightsLogger.trackException(error, {
                            component: 'AdminDashboard'
                        });
                        console.error('Load all users failed:', error);
                    })
                );
                
                promises.push(
                    loadAdminActivities().catch(error => {
                        appInsightsLogger.trackException(error, {
                            component: 'AdminDashboard'
                        });
                        console.error('Load admin activities failed:', error);
                    })
                );
                
                await Promise.allSettled(promises);
            } catch (error) {
                appInsightsLogger.trackException(error, {
                    component: 'AdminDashboard'
                });
                console.error('Error in data initialization:', error);
            } finally {
                setLoading(false);
                setInitialLoadComplete(true);
                setPeriodicRefreshEnabled(true); // Enable periodic refresh after initial load completes
            }
        };

        initializeData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run once on mount

    // Periodic refresh every 15 seconds when idle (only after initial load completes)
    useEffect(() => {
        if (!initialLoadComplete || !periodicRefreshEnabled || isPerformingAction) {
            return () => { }; // Return empty cleanup function
        }

        const interval = setInterval(() => {
            console.log('AdminDashboard: Periodic refresh triggered (15 seconds)');
            loadAllUsers().catch(error => {
                appInsightsLogger.trackException(error, {
                    component: 'AdminDashboard',
                    action: 'periodicRefresh'
                });
                console.error('Periodic refresh failed:', error);
            });
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, [initialLoadComplete, periodicRefreshEnabled, isPerformingAction, loadAllUsers]);

    // Initial load: show all users when data is first loaded
    useEffect(() => {
        if (allUsers.length > 0 && initialLoadComplete) {
            performClientSideSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allUsers.length, initialLoadComplete]);

    const handleSearch = () => {
        appInsightsLogger.trackEvent('Admin_User_Search_Initiated', {
            component: 'AdminDashboard',
            searchQuery: searchQuery,
            statusFilter: statusFilter,
            searchType: 'client-side'
        });

        // Perform client-side search/filtering
        performClientSideSearch();
    };

    // Sync displayedUsers with users, userPage, and userPageSize changes
    useEffect(() => {
        if (users.length > 0 && userPage > 0) {
            const startIndex = (userPage - 1) * userPageSize;
            const endIndex = startIndex + userPageSize;
            const paginatedUsers = users.slice(startIndex, endIndex);
            setDisplayedUsers(paginatedUsers);

            const totalPages = Math.ceil(users.length / userPageSize);
            setUserTotalPages(totalPages);
        } else if (users.length === 0) {
            // Clear displayed users when users array is empty
            setDisplayedUsers([]);
            setUserTotalPages(1);
        }
    }, [users, userPage, userPageSize]);

    const updateDisplayedUsers = useCallback((page) => {
        // Use functional updates to ensure we get the latest state values
        setUsers(currentUsers => {
            setUserPageSize(currentPageSize => {
                if (currentUsers.length > 0) {
                    const startIndex = (page - 1) * currentPageSize;
                    const endIndex = startIndex + currentPageSize;
                    const paginatedUsers = currentUsers.slice(startIndex, endIndex);
                    setDisplayedUsers(paginatedUsers);
                    const totalPages = Math.ceil(currentUsers.length / currentPageSize);
                    setUserTotalPages(totalPages);
                }
                setUserPage(page);
                return currentPageSize; // Return unchanged
            });
            return currentUsers; // Return unchanged
        });
    }, []);

    const handleLockUser = async (userId) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Lock_User_Attempt', {
                component: 'AdminDashboard',
                targetUserId: userId
            });

            await adminService.lockUser({
                targetUserId: userId,
                adminUserId: userDetails.userId,
                adminEmail: userDetails?.emailId,
                action: 'Lock',
                reason: 'Admin initiated lock'
            });

            // Immediate refresh after mutation
            const refreshedUsers = await loadAllUsers();
            performClientSideSearch(refreshedUsers, true);
            await loadAdminActivities();
            alert('User locked successfully');

            appInsightsLogger.trackEvent('Admin_Lock_User_Success', {
                component: 'AdminDashboard',
                targetUserId: userId
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'lockUser',
                targetUserId: userId
            });
            console.error('Error locking user:', error);
            alert('Failed to lock user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleUnlockUser = async (userId) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Unlock_User_Attempt', {
                component: 'AdminDashboard',
                targetUserId: userId
            });

            await adminService.unlockUser({
                targetUserId: userId,
                adminUserId: userDetails.userId,
                adminEmail: userDetails?.emailId,
                action: 'Unlock',
                reason: 'Admin initiated unlock'
            });

            // Immediate refresh after mutation
            const refreshedUsers = await loadAllUsers();
            performClientSideSearch(refreshedUsers, true);
            await loadAdminActivities();
            alert('User unlocked successfully');

            appInsightsLogger.trackEvent('Admin_Unlock_User_Success', {
                component: 'AdminDashboard',
                targetUserId: userId
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'unlockUser',
                targetUserId: userId
            });
            console.error('Error unlocking user:', error);
            alert('Failed to unlock user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleUnlockOktaUser = async (oktaUserId, username) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Unlock_Okta_User_Attempt', {
                component: 'AdminDashboard',
                targetOktaUserId: oktaUserId
            });

            await oktaService.unlockUser(oktaUserId);

            // Optimistic update - instant UI feedback
            updateUserStatusOptimistically(oktaUserId, 'ACTIVE');
            alert(`User '${username || oktaUserId}' unlocked successfully`);

            // Background refresh to get authoritative data from API
            loadAllUsers().then(refreshedUsers => {
                if (refreshedUsers) performClientSideSearch(refreshedUsers, true);
            }).catch(console.error);
            loadAdminActivities().catch(console.error);

            appInsightsLogger.trackEvent('Admin_Unlock_Okta_User_Success', {
                component: 'AdminDashboard',
                targetOktaUserId: oktaUserId
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'unlockOktaUser',
                targetOktaUserId: oktaUserId
            });
            console.error('Error unlocking Okta user:', error);
            alert('Failed to unlock user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleResetPassword = async (oktaUserId, userEmail) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            // Validate that we have a valid oktaUserId
            if (!oktaUserId || oktaUserId.trim().length === 0) {
                alert('Cannot reset password: User does not have a valid Okta ID');
                return;
            }

            appInsightsLogger.trackEvent('Admin_Reset_Password_Attempt', {
                component: 'AdminDashboard',
                targetOktaUserId: oktaUserId,
                targetUserEmail: userEmail
            });

            await oktaService.resetPassword(oktaUserId);

            alert(`Password reset email sent to ${userEmail}`);

            // Background refresh to get authoritative data from API
            loadAllUsers().then(refreshedUsers => {
                if (refreshedUsers) performClientSideSearch(refreshedUsers, true);
            }).catch(console.error);
            loadAdminActivities().catch(console.error);

            appInsightsLogger.trackEvent('Admin_Reset_Password_Success', {
                component: 'AdminDashboard',
                targetOktaUserId: oktaUserId,
                targetUserEmail: userEmail
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'resetPassword',
                targetOktaUserId: oktaUserId,
                targetUserEmail: userEmail
            });
            console.error('Error resetting password:', error);
            loadAdminActivities().catch(console.error);
            alert('Password reset failed. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleEnableUser = async (userId, currentStatus, username) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Enable_User_Attempt', {
                component: 'AdminDashboard',
                targetUserId: userId,
                currentStatus: currentStatus
            });

            await oktaService.enableUser(userId, currentStatus);

            // Optimistic update - instant UI feedback
            updateUserStatusOptimistically(userId, 'ACTIVE');
            alert(`User '${username || userId}' enabled successfully`);

            // Background refresh to get authoritative data from API
            loadAllUsers().then(refreshedUsers => {
                if (refreshedUsers) performClientSideSearch(refreshedUsers, true);
            }).catch(console.error);
            loadAdminActivities().catch(console.error);

            appInsightsLogger.trackEvent('Admin_Enable_User_Success', {
                component: 'AdminDashboard',
                targetUserId: userId,
                currentStatus: currentStatus
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'enableUser',
                targetUserId: userId,
                currentStatus: currentStatus
            });
            console.error('Error enabling user:', error);
            alert('Failed to enable user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleDisableUser = async (userId, username) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Disable_User_Attempt', {
                component: 'AdminDashboard',
                targetUserId: userId
            });

            await oktaService.disableUser(userId);

            // Optimistic update - instant UI feedback
            updateUserStatusOptimistically(userId, 'DEPROVISIONED');
            alert(`User '${username || userId}' disabled successfully`);

            // Background refresh to get authoritative data from API
            loadAllUsers().then(refreshedUsers => {
                if (refreshedUsers) performClientSideSearch(refreshedUsers, true);
            }).catch(console.error);
            loadAdminActivities().catch(console.error);

            appInsightsLogger.trackEvent('Admin_Disable_User_Success', {
                component: 'AdminDashboard',
                targetUserId: userId
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'disableUser',
                targetUserId: userId
            });
            console.error('Error disabling user:', error);
            alert('Failed to disable user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleSuspendUser = async (userId, username) => {
        // Pause periodic refresh during action
        setIsPerformingAction(true);
        setPeriodicRefreshEnabled(false);

        try {
            appInsightsLogger.trackEvent('Admin_Suspend_User_Attempt', {
                component: 'AdminDashboard',
                targetUserId: userId
            });

            await oktaService.suspendUser(userId);

            // Optimistic update - instant UI feedback
            updateUserStatusOptimistically(userId, 'SUSPENDED');
            alert(`User '${username || userId}' suspended successfully`);

            // Background refresh to get authoritative data from API
            loadAllUsers().then(refreshedUsers => {
                if (refreshedUsers) performClientSideSearch(refreshedUsers, true);
            }).catch(console.error);
            loadAdminActivities().catch(console.error);

            appInsightsLogger.trackEvent('Admin_Suspend_User_Success', {
                component: 'AdminDashboard',
                targetUserId: userId
            });
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'AdminDashboard',
                action: 'suspendUser',
                targetUserId: userId
            });
            console.error('Error suspending user:', error);
            alert('Failed to suspend user. Please try again.');
        } finally {
            // Resume periodic refresh
            setIsPerformingAction(false);
            setPeriodicRefreshEnabled(true);
        }
    };

    const handleEditUser = async (user) => {
        appInsightsLogger.trackEvent('Admin_Edit_User_Click', {
            component: 'AdminDashboard',
            targetUserId: user.userId,
            targetUserEmail: user.email
        });

        try {
            setEditingUser(user);
            setShowCreateUser(true);
            // Smooth scroll to top so user can see the edit form
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('Error while updating the User:', error);
            alert('Error updating user information. Please try again.');
        }
    };

    const userColumns = [
        {
            key: 'username',
            header: 'Username',
            render: (user) => {
                // Extract username from email (part before @) as fallback
                return user.username || user.email?.split('@')[0] || user.userId || '';
            }
        },
        { key: 'email', header: 'Email' },
        { key: 'organization', header: 'Organization' },
        {
            key: 'status',
            header: 'Status',
            render: (user) => {
                const statusLower = user.oktaStatus?.toLowerCase() || '';
                const isLocked = user.isLocked || statusLower === 'locked' || statusLower === 'locked_out';
                const status = isLocked ? 'Locked' : (user.oktaStatus || 'Active');
                let color = '#28a745';
                let backgroundColor = '#d4edda';

                if (isLocked) {
                    color = '#dc3545';
                    backgroundColor = '#f8d7da';
                } else if (statusLower === 'recovery') {
                    color = '#ff9800';
                    backgroundColor = '#fff4e5';
                } else if (statusLower === 'suspended') {
                    color = '#fd7e14';
                    backgroundColor = '#ffe5d0';
                } else if (statusLower === 'disabled' || statusLower === 'deprovisioned') {
                    color = '#6c757d';
                    backgroundColor = '#e2e3e5';
                } else if (statusLower === 'pending') {
                    color = '#ffc107';
                    backgroundColor = '#fff3cd';
                } else if (statusLower === 'provisioned' || statusLower === 'staged') {
                    color = '#17a2b8';
                    backgroundColor = '#d1ecf1';
                }

                return (
                    <span style={{
                        color: color,
                        fontWeight: 'bold',
                        padding: PADDING_SMALL,
                        borderRadius: '12px',
                        backgroundColor: backgroundColor,
                        fontSize: '12px',
                        textTransform: 'uppercase'
                    }}>
                        {status}
                    </span>
                );
            }
        },
        {
            key: 'lastLoginDate',
            header: 'Last Login',
            render: (user) => user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString() : 'Never'
        },
        {
            key: 'failedAttempts',
            header: 'Failed Attempts',
            render: (user) => user.failedLoginAttempts || 0
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (user) => {
                const statusLower = user.oktaStatus?.toLowerCase() || '';
                const isLocallyLocked = user.isLocked === true; // Locked in local DB
                const isOktaLockedOut = statusLower === 'locked_out'; // Locked in Okta
                const isLocked = isLocallyLocked || isOktaLockedOut || statusLower === 'locked';
                const isDisabled = statusLower === 'disabled' || statusLower === 'suspended';
                const isDeprovisioned = statusLower === 'deprovisioned';
                const isProvisioned = statusLower === 'provisioned';
                const isStaged = statusLower === 'staged';
                const isRecovery = statusLower === 'recovery';
                const isActive = statusLower === 'active' || (!isLocked && !isDisabled && !isDeprovisioned && !isProvisioned && !isStaged && !isRecovery);
                
                // Disable all actions when edit form is open
                const isEditModeActive = showCreateUser;
                const disabledStyle = isEditModeActive ? { opacity: 0.5, cursor: 'not-allowed' } : {};
                const disabledTitle = isEditModeActive ? 'Please cancel or save the current edit first' : '';

                const renderUserStatusButton = () => {
                    // STAGED -> Activate or Delete
                    if (isStaged) {
                        return (
                            <>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleEnableUser(user.oktaUserId, user.oktaStatus, user.username)}
                                    disabled={isEditModeActive}
                                    title={disabledTitle || 'Activate user'}
                                    style={{ fontSize: '10px', padding: '2px 6px', minWidth: '50px', height: '24px', ...disabledStyle }}
                                >
                                    Activate
                                </button>
                            </>
                        );
                    }

                    // PROVISIONED -> Deactivate only (no Activate button)
                    if (isProvisioned) {
                        return (
                            <button
                                className="btn btn-secondary"
                                onClick={() => handleDisableUser(user.oktaUserId, user.username)}
                                disabled={isEditModeActive}
                                title={disabledTitle || 'Deactivate user'}
                                style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', height: '24px', ...disabledStyle }}
                            >
                                Deactivate
                            </button>
                        );
                    }
                    // RECOVERY -> Activate
                    if (isRecovery) {
                        return (
                            <>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleEnableUser(user.oktaUserId, user.oktaStatus, user.username)}
                                    disabled={isEditModeActive}
                                    title={disabledTitle || 'Activate user'}
                                    style={{ fontSize: '10px', padding: '2px 6px', minWidth: '50px', height: '24px', ...disabledStyle }}
                                >
                                    Activate
                                </button>
                            </>
                        );
                    }

                    // LOCKED_OUT -> Unlock
                    if (isLocked) {
                        // Determine whether to use local DB unlock or Okta unlock
                        const handleUnlock = isOktaLockedOut 
                            ? () => handleUnlockOktaUser(user.oktaUserId, user.username)
                            : () => handleUnlockUser(user.userId);
                        
                        return (
                            <button
                                className="btn btn-success"
                                onClick={handleUnlock}
                                disabled={isEditModeActive}
                                title={disabledTitle || 'Unlock user'}
                                style={{ fontSize: '10px', padding: '2px 6px', minWidth: '50px', height: '24px', ...disabledStyle }}
                            >
                                Unlock
                            </button>
                        );
                    }

                    // SUSPENDED/DISABLED -> Unsuspend (Reactivate)
                    if (isDisabled) {
                        return (
                            <button
                                className="btn btn-success"
                                onClick={() => handleEnableUser(user.oktaUserId, user.oktaStatus, user.username)}
                                disabled={isEditModeActive}
                                title={disabledTitle || 'Unsuspend user'}
                                style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', height: '24px', ...disabledStyle }}
                            >
                                Unsuspend
                            </button>
                        );
                    }

                    // DEPROVISIONED -> Reactivate
                    if (isDeprovisioned) {
                        return (
                            <button
                                className="btn btn-success"
                                onClick={() => handleEnableUser(user.oktaUserId, user.oktaStatus, user.username)}
                                disabled={isEditModeActive}
                                title={disabledTitle || 'Reactivate user'}
                                style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', height: '24px', ...disabledStyle }}
                            >
                                Reactivate
                            </button>
                        );
                    }

                    // ACTIVE -> Suspend or Deactivate
                    if (isActive) {
                        return (
                            <>
                                <button
                                    className="btn btn-warning"
                                    onClick={() => handleSuspendUser(user.oktaUserId, user.username)}
                                    disabled={isEditModeActive}
                                    title={disabledTitle || 'Suspend user'}
                                    style={{ fontSize: '10px', padding: '2px 6px', minWidth: '50px', height: '24px', ...disabledStyle }}
                                >
                                    Suspend
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => handleDisableUser(user.oktaUserId, user.username)}
                                    disabled={isEditModeActive}
                                    title={disabledTitle || 'Deactivate user'}
                                    style={{ fontSize: '10px', padding: '2px 6px', minWidth: '60px', height: '24px', ...disabledStyle }}
                                >
                                    Deactivate
                                </button>
                            </>
                        );
                    }

                    return null;
                };
                
                const hasValidOktaUserId = user.oktaUserId && user.oktaUserId.trim().length > 0;
                const resetPwdDisabled = !hasValidOktaUserId || isEditModeActive;
                const resetPwdTitle = isEditModeActive ? disabledTitle : (!hasValidOktaUserId ? 'User does not have a valid Okta ID' : 'Reset user password');

                return (
                    <div className="action-buttons" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {renderUserStatusButton()}
                        <button
                            className="btn btn-info"
                            onClick={() => handleResetPassword(user.oktaUserId, user.email)}
                            disabled={resetPwdDisabled}
                            title={resetPwdTitle}
                            style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                minWidth: '70px',
                                height: '24px',
                                opacity: resetPwdDisabled ? 0.5 : 1,
                                cursor: resetPwdDisabled ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Reset Password
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => handleEditUser(user)}
                            disabled={isEditModeActive}
                            title={disabledTitle || 'Edit user'}
                            style={{ fontSize: '10px', padding: '2px 6px', minWidth: '40px', height: '24px', ...disabledStyle }}
                        >
                            Edit
                        </button>
                    </div>
                );
            }
        }
    ];

  const adminDisplayName = getAdminDisplayName();

  // Show loading spinner if still loading (check allUsers instead of filtered users)
  if (loading && allUsers.length === 0 && !initialLoadComplete) {
    return (
      <div className="admin-dashboard">
        <div className="admin-container">
          <div className="admin-header">
            <img src={`${import.meta.env.BASE_URL}evernorth-logo.svg`} alt="Evernorth Logo" className="admin-logo" />
            <h1 className="admin-title">Embarc Admin Portal - Operations Dashboard</h1>
            <div className="admin-user-info">
              <span className="admin-username" title={adminDisplayName}>{adminDisplayName}</span>
              <span className="admin-separator">|</span>
              <LogoutButton 
                className="admin-logout-btn"
              />
            </div>
          </div>
          <div style={{
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px'
          }}>
            <LoadingSpinner size="large" />
            <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
              Loading admin dashboard...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        <div className="admin-header">
          <img src={`${import.meta.env.BASE_URL}evernorth-logo.svg`} alt="Evernorth Logo" className="admin-logo" />
          <h1 className="admin-title">Embarc Admin Portal - Operations Dashboard</h1>
          <div className="admin-user-info">
            <span className="admin-username" title={adminDisplayName}>{adminDisplayName}</span>
            <span className="admin-separator">|</span>
            <LogoutButton 
              className="admin-logout-btn"
            />
          </div>
        </div>

                <div className="user-management-card">
                    <h4>User Account Search & Management</h4>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <button
                            className="btn"
                            onClick={() => {
                                setShowCreateUser((s) => !s);
                                // Clear editing user when toggling create user form
                                if (!showCreateUser) {
                                    setEditingUser(null);
                                }
                            }}
                            style={{ backgroundColor: '#009688', color: 'white', border: 'none', padding: '6px 12px', borderRadius: BORDER_RADIUS_SMALL }}
                        >
                            {showCreateUser ? (editingUser ? 'Cancel Edit' : 'Hide Create User') : 'Create User'}
                        </button>
                    </div>

                    {showCreateUser && (
                      <OktaUserRegistration
                        editingUser={editingUser}
                        onCreated={async () => { 
                            setShowCreateUser(false); 
                            setEditingUser(null); 
                            const refreshedUsers = await loadAllUsers();
                            performClientSideSearch(refreshedUsers, true);
                            await loadAdminActivities();
                        }}
                        onCancel={() => {
                            setShowCreateUser(false);
                            setEditingUser(null);
                        }}
                      />
                   )}

                    <div className="search-controls">
                        <div className="search-row">
                            <div className="search-field" style={{ flex: '1', minWidth: '200px' }}>
                                <label htmlFor="searchBy">Search by:</label>
                                <select
                                    id="searchBy"
                                    className="form-control"
                                    value={searchBy}
                                    onChange={e => setSearchBy(e.target.value)}
                                >
                                    <option value="Username">Username</option>
                                    <option value="Email">Email</option>
                                    <option value="Organization">Organization</option>
                                </select>
                            </div>
                            <div className="search-field" style={{ flex: '2', minWidth: '300px' }}>
                                <label htmlFor="searchValue">Search Value:</label>
                                <input
                                    id="searchValue"
                                    type="text"
                                    placeholder="Enter search criteria"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="form-control"
                                />
                            </div>
                            <div className="search-field" style={{ flex: '1', minWidth: '150px' }}>
                                <label htmlFor="accountStatus">Account Status:</label>
                                <select
                                    id="accountStatus"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="form-control"
                                >
                                    <option value="All">All</option>
                                    <option value="Active">Active</option>
                                    <option value="Locked">Locked</option>
                                    <option value="Provisioned">Provisioned</option>
                                    <option value="Suspended">Suspended</option>
                                    <option value="Deprovisioned">Deprovisioned</option>
                                    <option value="Recovery">Recovery</option>
                                </select>
                            </div>
                            <div className="search-button" style={{ alignSelf: 'end' }}>
                                <button
                                    onClick={handleSearch}
                                    className="btn"
                                    style={{
                                        backgroundColor: '#009688',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: BORDER_RADIUS_SMALL
                                    }}>
                  Search
                </button>
              </div>
            </div>
          </div>

                    <h4>User Account Results</h4>
                    <DataTable
                        data={displayedUsers}
                        columns={userColumns}
                        loading={loading}
                        emptyMessage="No users found"
                    />

                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 16px' }}>
                        <div className="page-size-selector">
                            <label htmlFor="userPageSize" style={{ marginRight: '8px' }}>Show: </label>
                            <select
                                id="userPageSize"
                                value={userPageSize}
                                onChange={(e) => {
                                    const newPageSize = Number(e.target.value);
                                    setUserPageSize(newPageSize);
                                    updateDisplayedUsers(1);
                                }}
                                style={{ padding: PADDING_SMALL, borderRadius: BORDER_RADIUS_SMALL, border: BORDER_STANDARD }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                            </select>
                            <span style={{ marginLeft: '8px' }}>per page</span>
                        </div>

                        <div className="pagination-buttons">
                            <button
                                onClick={() => {
                                    const newPage = userPage - 1;
                                    updateDisplayedUsers(newPage);
                                }}
                                disabled={userPage === 1}
                                style={{
                                    padding: '6px 12px',
                                    marginRight: '8px',
                                    borderRadius: BORDER_RADIUS_SMALL,
                                    border: BORDER_STANDARD,
                                    backgroundColor: userPage === 1 ? '#f0f0f0' : '#fff',
                                    cursor: userPage === 1 ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
                                }}
                            >
                                Previous
                            </button>
                            <span style={{ margin: '0 16px', fontWeight: 'bold' }}>
                                Page {userPage} of {userTotalPages}
                            </span>
                            <button
                                onClick={() => {
                                    const newPage = userPage + 1;
                                    updateDisplayedUsers(newPage);
                                }}
                                disabled={userPage >= userTotalPages}
                                style={{
                                    padding: '6px 12px',
                                    marginLeft: '8px',
                                    borderRadius: BORDER_RADIUS_SMALL,
                                    border: BORDER_STANDARD,
                                    backgroundColor: userPage >= userTotalPages ? '#f0f0f0' : '#fff',
                                    cursor: userPage >= userTotalPages ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div className="activity-log-panel">
                    <h3>Recent Admin Activities</h3>
                    <div className="activity-table-container">
                        <table className="activity-table">
                            <thead className="activity-table-header">
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Admin</th>
                                    <th>Action</th>
                                    <th>Target User</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adminActivities.length > 0 ? (
                                    adminActivities.map((activity, index) => (
                                        <tr key={`activity-${activity.id || activity.createdDate || index}`}>
                                            <td>
                                                {new Date(activity.createdDate || activity.timestamp).toLocaleString()}
                                            </td>
                                            <td>
                                                {activity.adminEmail || activity.performedBy || 'Admin'}
                                            </td>
                                            <td>
                                                {activity.action || activity.actionType}
                                            </td>
                                            <td>
                                                {activity.targetUserEmail || activity.user || activity.targetUserId || 'N/A'}
                                            </td>
                                            <td>
                                                <span className={`activity-status ${(activity.status === 'Success' || activity.result === 'Success' ||
                                                    (!activity.status && !activity.result)) ? 'success' : 'failed'
                                                    }`}>
                                                    {activity.status || activity.result || 'Success'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="no-activities">
                                            No recent activities found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 16px' }}>
                        <div className="page-size-selector">
                            <label htmlFor="activityPageSize" style={{ marginRight: '8px' }}>Show: </label>
                            <select
                                id="activityPageSize"
                                value={activityPageSize}
                                onChange={(e) => {
                                    const newPageSize = Number(e.target.value);
                                    setActivityPageSize(newPageSize);
                                    setActivityPage(1);
                                    loadAdminActivities(1, newPageSize);
                                }}
                                style={{ padding: PADDING_SMALL, borderRadius: BORDER_RADIUS_SMALL, border: BORDER_STANDARD }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                            </select>
                            <span style={{ marginLeft: '8px' }}>per page</span>
                        </div>

                        <div className="pagination-buttons">
                            <button
                                onClick={() => {
                                    const newPage = activityPage - 1;
                                    setActivityPage(newPage);
                                    loadAdminActivities(newPage, activityPageSize);
                                }}
                                disabled={activityPage === 1}
                                style={{
                                    padding: '6px 12px',
                                    marginRight: '8px',
                                    borderRadius: BORDER_RADIUS_SMALL,
                                    border: BORDER_STANDARD,
                                    backgroundColor: activityPage === 1 ? '#f0f0f0' : '#fff',
                                    cursor: activityPage === 1 ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
                                }}
                            >
                                Previous
                            </button>
                            <span style={{ margin: '0 16px', fontWeight: 'bold' }}>
                                Page {activityPage} of {activityTotalPages}
                            </span>
                            <button
                                onClick={() => {
                                    const newPage = activityPage + 1;
                                    setActivityPage(newPage);
                                    loadAdminActivities(newPage, activityPageSize);
                                }}
                                disabled={activityPage >= activityTotalPages}
                                style={{
                                    padding: '6px 12px',
                                    marginLeft: '8px',
                                    borderRadius: BORDER_RADIUS_SMALL,
                                    border: BORDER_STANDARD,
                                    backgroundColor: activityPage >= activityTotalPages ? '#f0f0f0' : '#fff',
                                    cursor: activityPage >= activityTotalPages ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AdminDashboard.propTypes = {
    userDetails: PropTypes.shape({
        username: PropTypes.string,
        role: PropTypes.string,
        passwordModifiedDate: PropTypes.string
    })
};

export default AdminDashboard;
